import { createSlice } from '@reduxjs/toolkit';
import { getDefaultPosition, getOptimalScale } from '../../utils/personImageUtils';

// Initialize state from localStorage (only UI state, not image data)
const loadPersistedState = () => {
  try {
    const savedState = localStorage.getItem('personImageUIState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      // Validate the structure and only load UI state
      if (parsed && typeof parsed === 'object') {
        // Migrate old createdAt to created_at in filters
        const filters = parsed.filters || {
          sortBy: 'created_at:desc',
          showActive: true
        };

        // Fix old camelCase column names to snake_case
        if (filters.sortBy && filters.sortBy.includes('createdAt')) {
          filters.sortBy = filters.sortBy.replace('createdAt', 'created_at');
          console.log('[PersonImage] Migrated sortBy from createdAt to created_at');
        }
        if (filters.sortBy && filters.sortBy.includes('updatedAt')) {
          filters.sortBy = filters.sortBy.replace('updatedAt', 'updated_at');
          console.log('[PersonImage] Migrated sortBy from updatedAt to updated_at');
        }

        return {
          position: parsed.position || { x: 0, y: 0 },
          scale: parsed.scale || 1.0,
          canvasSize: parsed.canvasSize || { width: 800, height: 600 },
          settings: parsed.settings || {
            autoCenter: true,
            autoScale: true,
            preserveAspectRatio: true,
            maxScale: 2.0,
            minScale: 0.1
          },
          filters,
          isCanvasIntegrated: parsed.isCanvasIntegrated || false,
          // Don't persist loading/error states or image data
          isLoading: false,
          error: null,
          uploadProgress: 0
        };
      }
    }
  } catch (error) {
    console.warn('Failed to load persisted person image UI state:', error);
    // Clear corrupted state
    try {
      localStorage.removeItem('personImageUIState');
    } catch (clearError) {
      console.warn('Failed to clear corrupted state:', clearError);
    }
  }
  return null;
};

const persistedState = loadPersistedState();

const defaultState = {
  // Active person image (from RTK Query)
  activePersonImageId: null,

  // Image data (for VTO results)
  image: null,
  originalImage: null,
  originalPersonImageDeleted: false, // Track if original person image was deleted

  // Position and scale (UI state)
  position: { x: 0, y: 0 },
  scale: 1.0,

  // Virtual try-on state
  isVirtualTryOnResult: false,
  virtualTryOnMetadata: null,

  // UI state
  isLoading: false,
  uploadProgress: 0,
  error: null,

  // Canvas integration state
  isCanvasIntegrated: false,

  // Canvas context
  canvasSize: { width: 800, height: 600 }, // Default canvas size

  // Settings
  settings: {
    autoCenter: true,
    autoScale: true,
    preserveAspectRatio: true,
    maxScale: 2.0,
    minScale: 0.1
  },

  // UI filters and preferences
  filters: {
    sortBy: 'created_at:desc',
    showActive: true
  }
};

// Merge persisted state with default state to ensure all properties exist
const initialState = persistedState ? {
  ...defaultState,
  ...persistedState,
  // Ensure nested objects are properly merged
  settings: {
    ...defaultState.settings,
    ...(persistedState.settings || {})
  },
  filters: {
    ...defaultState.filters,
    ...(persistedState.filters || {})
  }
} : defaultState;

export const personImageSlice = createSlice({
  name: 'personImage',
  initialState,
  reducers: {
    // Active person image management (works with RTK Query)
    setActivePersonImage: (state, action) => {
      const { personImageId, imageDimensions, imageUrl } = action.payload;

      state.activePersonImageId = personImageId;

      // Set the image URL for canvas display if provided
      if (imageUrl) {
        state.image = imageUrl;
      }

      state.isVirtualTryOnResult = false;
      state.virtualTryOnMetadata = null;
      state.error = null;
      state.originalImage = null; // Clear original image when switching person

      // Auto-position and scale if enabled and we have dimensions
      if (imageDimensions && state.settings.autoCenter && state.canvasSize.width > 0) {
        state.position = getDefaultPosition(
          state.canvasSize.width,
          state.canvasSize.height,
          imageDimensions.width,
          imageDimensions.height,
          state.scale
        );
      }

      if (imageDimensions && state.settings.autoScale && state.canvasSize.width > 0) {
        state.scale = getOptimalScale(
          state.canvasSize,
          imageDimensions,
          state.settings.maxScale
        );
      }
    },

    clearActivePersonImage: (state) => {
      state.activePersonImageId = null;
      state.image = null; // Clear any VTO result image
      state.originalImage = null;
      state.originalPersonImageDeleted = false; // Reset deleted flag
      state.position = { x: 0, y: 0 };
      state.scale = 1.0;
      state.isVirtualTryOnResult = false;
      state.virtualTryOnMetadata = null;
      state.isCanvasIntegrated = false; // Clear canvas integration when removing person image
      state.error = null;
    },

    // Mark original person image as deleted (for handling deleted items)
    markOriginalPersonImageDeleted: (state) => {
      state.originalPersonImageDeleted = true;
      console.log('[PersonImage] Original person image marked as deleted');
    },

    // Position management
    updatePosition: (state, action) => {
      const { x, y } = action.payload;
      state.position = { x, y };
    },

    movePosition: (state, action) => {
      const { deltaX, deltaY } = action.payload;
      state.position.x += deltaX;
      state.position.y += deltaY;
    },

    centerImage: (state) => {
      if (state.metadata?.originalDimensions && state.canvasSize.width > 0) {
        state.position = getDefaultPosition(
          state.canvasSize.width,
          state.canvasSize.height,
          state.metadata.originalDimensions.width,
          state.metadata.originalDimensions.height,
          state.scale
        );
      } else {
        // Default centering without dimensions
        state.position = { x: state.canvasSize.width / 2, y: state.canvasSize.height / 2 };
      }
    },

    // Scale management
    updateScale: (state, action) => {
      const newScale = Math.max(
        state.settings.minScale,
        Math.min(state.settings.maxScale, action.payload)
      );
      state.scale = newScale;

      // Re-center if auto-center is enabled
      if (state.settings.autoCenter && state.metadata?.originalDimensions) {
        state.position = getDefaultPosition(
          state.canvasSize.width,
          state.canvasSize.height,
          state.metadata.originalDimensions.width,
          state.metadata.originalDimensions.height,
          newScale
        );
      }
    },

    scaleBy: (state, action) => {
      const factor = action.payload;
      const newScale = Math.max(
        state.settings.minScale,
        Math.min(state.settings.maxScale, state.scale * factor)
      );
      state.scale = newScale;
    },

    resetScale: (state) => {
      if (state.metadata?.originalDimensions && state.canvasSize.width > 0) {
        state.scale = getOptimalScale(
          state.canvasSize,
          state.metadata.originalDimensions,
          state.settings.maxScale
        );
      } else {
        state.scale = 1.0;
      }
    },

    // Canvas context
    updateCanvasSize: (state, action) => {
      const { width, height } = action.payload;
      state.canvasSize = { width, height };

      // Auto-adjust position and scale if image exists
      if (state.image && state.metadata?.originalDimensions) {
        if (state.settings.autoCenter) {
          state.position = getDefaultPosition(width, height,
            state.metadata.originalDimensions.width,
            state.metadata.originalDimensions.height,
            state.scale
          );
        }

        if (state.settings.autoScale) {
          state.scale = getOptimalScale(
            { width, height },
            state.metadata.originalDimensions,
            state.settings.maxScale
          );
        }
      }
    },

    // Virtual try-on results
    setVirtualTryOnResult: (state, action) => {
      const { image, metadata = {}, originalImage } = action.payload;

      console.log('[PersonImage] setVirtualTryOnResult reducer called:', {
        currentImage: state.image?.substring(0, 50),
        newImage: image?.substring(0, 50),
        hasOriginal: !!state.originalImage,
        isCurrentlyVTO: state.isVirtualTryOnResult
      });

      // Store original image if provided, or if not already stored use current
      if (originalImage) {
        state.originalImage = originalImage;
      } else if (!state.originalImage && state.image) {
        state.originalImage = state.image;
      }

      // Update the displayed image with VTO result
      if (image) {
        state.image = image;
        console.log('[PersonImage] State image updated to:', image.substring(0, 50));
      } else {
        console.warn('[PersonImage] No image provided to setVirtualTryOnResult');
      }

      state.isVirtualTryOnResult = true;
      state.virtualTryOnMetadata = {
        ...metadata,
        timestamp: Date.now()
      };
      state.error = null;
    },

    revertToOriginal: (state) => {
      // Check if original person image was deleted
      if (state.originalPersonImageDeleted) {
        // Original image was deleted, clear everything
        state.image = null;
        state.originalImage = null;
        state.activePersonImageId = null;
        console.log('[PersonImage] Original person image was deleted, showing empty canvas');
      } else if (state.originalImage) {
        // Restore original image if available
        state.image = state.originalImage;
        state.originalImage = null;
        console.log('[PersonImage] Reverted to original person image');
      } else {
        // No original image stored, just clear VTO result
        state.image = null;
        console.log('[PersonImage] No original image to revert to, clearing VTO result');
      }

      state.isVirtualTryOnResult = false;
      state.virtualTryOnMetadata = null;
    },

    // Filters
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    setSortBy: (state, action) => {
      state.filters.sortBy = action.payload;
    },

    setShowActive: (state, action) => {
      state.filters.showActive = action.payload;
    },

    // Loading and error states
    setLoading: (state, action) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setUploadProgress: (state, action) => {
      state.uploadProgress = Math.max(0, Math.min(100, action.payload));
    },

    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
      state.uploadProgress = 0;
    },

    clearError: (state) => {
      state.error = null;
    },

    // Settings
    updateSettings: (state, action) => {
      state.settings = {
        ...state.settings,
        ...action.payload
      };
    },

    // Batch updates for performance
    batchUpdate: (state, action) => {
      const updates = action.payload;
      Object.keys(updates).forEach(key => {
        if (key in state) {
          state[key] = updates[key];
        }
      });
    },

    // Reset to initial state
    resetPersonImageState: (state) => {
      Object.assign(state, {
        ...initialState,
        canvasSize: state.canvasSize, // Preserve canvas size
        settings: state.settings // Preserve settings
      });
    },

    // Set canvas integration state
    setCanvasIntegrationMode: (state, action) => {
      state.isCanvasIntegrated = action.payload;
    }
  }
});

export const {
  setActivePersonImage,
  clearActivePersonImage,
  markOriginalPersonImageDeleted,
  updatePosition,
  movePosition,
  centerImage,
  updateScale,
  scaleBy,
  resetScale,
  updateCanvasSize,
  setVirtualTryOnResult,
  revertToOriginal,
  setFilters,
  setSortBy,
  setShowActive,
  setLoading,
  setUploadProgress,
  setError,
  clearError,
  updateSettings,
  batchUpdate,
  resetPersonImageState,
  setCanvasIntegrationMode
} = personImageSlice.actions;

// Persistence middleware (only for UI state, not image data)
export const persistPersonImageState = (store) => (next) => (action) => {
  const result = next(action);

  // Only persist on person image actions
  if (action.type.startsWith('personImage/')) {
    const state = store.getState().personImage;

    // Create a clean UI state object for persistence (no image data)
    const persistState = {
      // Only persist UI state and preferences
      activePersonImageId: state.activePersonImageId, // Just the ID, not the image data
      position: state.position,
      scale: state.scale,
      isCanvasIntegrated: state.isCanvasIntegrated,
      canvasSize: state.canvasSize,
      settings: state.settings,
      filters: state.filters
    };

    try {
      localStorage.setItem('personImageUIState', JSON.stringify(persistState));
    } catch (error) {
      console.warn('Failed to persist person image UI state:', error);

      // If quota exceeded, try to clear some space
      if (error.name === 'QuotaExceededError') {
        try {
          // Clear old state and try again
          localStorage.removeItem('personImageUIState');
          localStorage.removeItem('personImageState'); // Remove old key too
          localStorage.setItem('personImageUIState', JSON.stringify(persistState));
        } catch (retryError) {
          console.error('Still failed to persist after clearing space:', retryError);
        }
      }
    }
  }

  return result;
};

export default personImageSlice.reducer;